import { Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import {Observable, EMPTY, throwError} from 'rxjs';

import { environment } from '../../environments/environment';
import { AuthService } from '@services';
import {catchError, map, takeUntil, tap} from 'rxjs/operators';
import { OidcHttpService } from '../services/oidc/oidc-http.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private BASE_URL: string = environment.apiBaseURL;

  private unauthorisedCount: number = 0;
  private unauthorisedMax: number = 5;

  constructor(
    private auth: AuthService,
    private injector: Injector
  ) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.includes('/assets/')) {
      return next.handle(req);
    }

    const requestUrl = new URL(req.url, window.location.origin);
    const authority = new URL(environment.oidcConfig.authority);
    if (requestUrl.origin === authority.origin) {
      return next.handle(req).pipe(catchError(error => {
        // Preserve the structured OAuth rejection before the library wraps it.
        if (this.auth.shouldUseOidc() && typeof req.body === 'string' &&
            new URLSearchParams(req.body).get('grant_type') === 'refresh_token' &&
            error.error?.error === 'invalid_grant') {
          this.auth.oidc.recordRefreshRejection();
        }
        return throwError(() => error);
      }));
    }
    
    if (this.auth.isOidcMode()) {
      return this.injector.get(OidcHttpService).intercept(req, next);
    }

    const preservingLegacy = this.auth.preservingLegacySession;
    const protectedApi = [environment.apiBaseURL, environment.opogoPlatformApiUrl].some(base => {
      const api = new URL(base, window.location.origin);
      const path = api.pathname.replace(/\/$/, '');
      return requestUrl.origin === api.origin &&
        (requestUrl.pathname === path || requestUrl.pathname.startsWith(path + '/'));
    });
    if (preservingLegacy && protectedApi && !this.auth.hasValidLegacySession) {
      this.auth.resumeWithOidc();
      return EMPTY;
    }

    const excludedUrl = [
      `${this.BASE_URL}/contents/PartnerUploadMedia`,
      `${this.BASE_URL}/staffmanagement/bulkImportStaff`,
      `${this.BASE_URL}/clients/reference`,
      `${this.BASE_URL}/candidate/profile/image/update`,
      `${this.BASE_URL}/candidate/UploadCV`
    ];

    if (excludedUrl.some(x => req.url.startsWith(x))) {
      return next.handle(req);
    }

    const requestToken = this.auth.token;
    if (requestToken !== null) {

      const headers = new HttpHeaders({
        'Authorization': `bearer ${requestToken}`,
        'Content-Type': 'application/json',
        'Api-Version': '1.0'
      });

      req = req.clone({headers});
    }

    if (preservingLegacy) {
      return next.handle(req).pipe(
        catchError(error => {
          if (protectedApi && error.status === 401) {
            if (this.auth.token === requestToken) this.auth.resumeWithOidc();
            return EMPTY;
          }
          return throwError(() => error);
        }),
        takeUntil(this.auth.LogOutEvent),
      );
    }

    return next.handle(req).pipe(tap(
      (event: HttpEvent<any>) => event,
      (error: any) => {
        if (error instanceof HttpErrorResponse) {
          return this.handleLegacyError(error, req, next);
        }
      }
    ));
  }

  private handleLegacyError(error: Response | any, req: HttpRequest<any>, next: HttpHandler) {
    if (error.status === 401) {
      this.unauthorisedCount++;

      if (this.unauthorisedCount == this.unauthorisedMax) {
          this.auth.logout();
          this.unauthorisedCount = 0;
          return EMPTY;
      } 

      return this.auth.refreshToken(this.auth.refToken).pipe(
        map((response) => {
          const responseJson = response.json();

          this.auth.pipeLogin(responseJson.access_token, responseJson.refresh_token, responseJson['.expires']);

          const headers = new HttpHeaders({
            'Authorization': `bearer ${this.auth.token}`,
            'Content-Type': 'application/json',
            'Api-Version': '1.0'
          });
    
          req = req.clone({headers});

          return next.handle(req);
        }),
        catchError(err => {
          return next.handle(req);
        })
      );
    }

    return next.handle(req);
  }

}
